import './LastJobContent.scss';
import './cards/Cards.css';

import { IonCol, IonGrid, IonRow } from '@ionic/react';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { JobMessages } from 'src/intl/JobMessages';
import { TimeTable, msToTime } from 'src/utils/logs/logHelpers';
import { JobLogV2Data } from 'src/utils/logs/types';
import { GenericMessages } from '../../intl/GenericMessages';
import { DqText } from '../dq-text';
import { PieSentinelSystem } from '../visualizations/pieSentinelSystem';
import { JobLog } from 'src/models/jobLog';
import { RangeSet } from '../visualizations/utils';
import { getControllerOffRanges } from '../../pages/job-info/util';

function shorten(value: any) {
  const length = 30;
  if (value == null) {
    return '';
  }
  if (value.length <= length) {
    return value;
  }
  value = value.substring(0, length);
  return value + '...';
}

interface LastJobContentProps {
  description: string;
  status: Exclude<JobLog['status'], null | undefined>;
  data: JobLogV2Data;
  userOrgType?: string;
}

export const LastJobContent: React.FC<LastJobContentProps> = ({ description, status, data, userOrgType }) => {
  const { formatMessage, formatDate, formatNumber } = useIntl();
  const notAvailable = formatMessage(GenericMessages.notAvailablePlaceholder);

  const lastTubeCount = data.tubeCount[data.tubeCount.length - 1];
  const tubesCleanedAttempted = lastTubeCount
    ? `${lastTubeCount.cleaned}/${lastTubeCount.cleaned + lastTubeCount.partial}`
    : undefined;
  const activeRunTimeMs = data.msElapsed;
  const tubesPerHour = data.cleanRate[data.cleanRate.length - 1]?.tubesPerHour;

  const timeTable = useMemo(() => {
    if (!data.timeTable.length) return undefined;
    return TimeTable.from(data.timeTable);
  }, [data.timeTable]);

  const { timeStart, timeEnd } = useMemo(() => {
    return {
      timeStart: timeTable?.getInstant(0),
      timeEnd: timeTable?.getInstant(data.msElapsed),
    };
  }, [data.msElapsed, timeTable]);

  const controllerOffRanges = useMemo<RangeSet>(() => {
    const powerEvents = data?.powerEvents || [];
    const msElapsed = data?.msElapsed || 0;
    return getControllerOffRanges(powerEvents, msElapsed);
  }, [data]);

  return (
    <div className="last-job-content-container">
      <IonGrid>
        <IonRow key="description">
          <IonCol className="last-content-column">
            <DqText>
              <FormattedMessage {...GenericMessages.descriptionLabel} />
            </DqText>
          </IonCol>
          <IonCol className="last-content-column">
            <DqText>{shorten(description)}</DqText>
          </IonCol>
        </IonRow>
        <IonRow key="start">
          <IonCol className="last-content-column">
            <DqText>{formatMessage(JobMessages.startDate)}</DqText>
          </IonCol>
          <IonCol className="last-content-column">
            <DqText>{timeStart ? formatDate(timeStart) : notAvailable}</DqText>
          </IonCol>
        </IonRow>
        <IonRow key="end">
          <IonCol className="last-content-column">
            <DqText>{formatMessage(JobMessages.endDate)}</DqText>
          </IonCol>
          <IonCol className="last-content-column">
            <DqText>{timeEnd && status === 'complete' ? formatDate(timeEnd) : notAvailable}</DqText>
          </IonCol>
        </IonRow>
        <IonRow key="runTime">
          <IonCol className="last-content-column">
            <DqText>{formatMessage(JobMessages.activeRunTime)}</DqText>
          </IonCol>
          <IonCol className="last-content-column">
            <DqText>{msToTime(activeRunTimeMs, formatNumber)}</DqText>
          </IonCol>
        </IonRow>
        <IonRow key="controller">
          <IonCol className="last-content-column">
            <DqText>{formatMessage(JobMessages.tubes)}</DqText>
          </IonCol>
          <IonCol className="last-content-column">
            <DqText>{tubesCleanedAttempted ?? notAvailable}</DqText>
          </IonCol>
        </IonRow>
        <IonRow key="tubesPerHour">
          <IonCol className="last-content-column">
            <DqText>{formatMessage(JobMessages.tubesPerHour)}</DqText>
          </IonCol>
          <IonCol className="last-content-column">
            <DqText>{tubesPerHour ?? notAvailable}</DqText>
          </IonCol>
        </IonRow>
      </IonGrid>
      {userOrgType === 'contractor' ? (
        <div className="last-job-content-pie-chart">
          <PieSentinelSystem
            title={formatMessage(JobMessages.systemHeading)}
            valve5={data.valveStatus[4]}
            valve2={data.valveStatus[1]}
            valve6={data.valveStatus[5]}
            timeTable={data.timeTable}
            timeRange={{ min: 0, max: data.msElapsed }}
            showTimeDetails
            controllerOffRanges={controllerOffRanges}
            logFileTimeRangeMin={0}
            logFileTimeRangeMax={activeRunTimeMs}
          />
        </div>
      ) : null}
    </div>
  );
};
